import React from "react";
import { Document, Page, Text, View, StyleSheet, Font } from "@react-pdf/renderer";
import type { GeneratedSlides, Slide } from "./types";
import { COLOR_VALUES, BG_COLOR_VALUES } from "./constants";

const styles = StyleSheet.create({
  page: {
    padding: 0,
    backgroundColor: "#FFFFFF",
    fontFamily: "Helvetica",
  },
  slideContainer: {
    width: "100%",
    height: "100%",
    padding: 40,
    display: "flex",
    flexDirection: "column",
    justifyContent: "center",
    position: "relative",
  },
  titleSlide: {
    textAlign: "center",
  },
  title: {
    fontSize: 32,
    fontWeight: "bold",
    marginBottom: 20,
  },
  subtitle: {
    fontSize: 18,
    color: "#555",
    marginBottom: 10,
  },
  contentTitle: {
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 20,
    borderBottomWidth: 2,
    paddingBottom: 5,
  },
  contentList: {
    marginTop: 10,
  },
  contentItem: {
    fontSize: 14,
    marginBottom: 10,
    display: "flex",
    flexDirection: "row",
  },
  bullet: {
    width: 15,
    marginRight: 5,
  },
  footer: {
    position: "absolute",
    bottom: 20,
    left: 40,
    right: 40,
    fontSize: 10,
    color: "#999",
    display: "flex",
    flexDirection: "row",
    justifyContent: "space-between",
  },
  decoration: {
    position: "absolute",
    top: 0,
    right: 0,
    width: 100,
    height: 100,
    opacity: 0.1,
  }
});

interface SlidesPDFProps {
  data: GeneratedSlides;
}

export const SlidesPDF: React.FC<SlidesPDFProps> = ({ data }) => {
  const primaryColor = COLOR_VALUES[data.colorScheme] || "#1967d2";
  const bgColor = BG_COLOR_VALUES[data.colorScheme] || "#e8f0fe";

  return (
    <Document>
      {data.slides.map((slide, index) => (
        <Page key={index} size="A4" orientation="landscape" style={[styles.page, { backgroundColor: index === 0 ? bgColor : "#FFFFFF" }]}>
          <View style={styles.slideContainer}>
            <View style={[styles.decoration, { backgroundColor: primaryColor, borderRadius: 50 }]} />

            {slide.type === "title" || index === 0 ? (
              <View style={styles.titleSlide}>
                <Text style={[styles.title, { color: primaryColor }]}>{slide.title}</Text>
                <Text style={styles.subtitle}>{data.subject} - {data.topic}</Text>
                <Text style={{ fontSize: 12, color: "#777" }}>{data.audience}</Text>
              </View>
            ) : (
              <View>
                <Text style={[styles.contentTitle, { color: primaryColor, borderBottomColor: primaryColor }]}>
                  {slide.title}
                </Text>
                <View style={styles.contentList}>
                  {Array.isArray(slide.content) 
                    ? slide.content.map((item, i) => (
                        <View key={i} style={styles.contentItem}>
                          <Text style={[styles.bullet, { color: primaryColor }]}>•</Text>
                          <Text style={{ flex: 1 }}>{item}</Text>
                        </View>
                      ))
                    : slide.content.split("\n").filter(l => l.trim()).map((item, i) => (
                        <View key={i} style={styles.contentItem}>
                          <Text style={[styles.bullet, { color: primaryColor }]}>•</Text>
                          <Text style={{ flex: 1 }}>{item.replace(/^[•\-\*]\s*/, "")}</Text>
                        </View>
                      ))
                  }
                </View>
              </View>
            )}

            <View style={styles.footer}>
              <Text>{data.title}</Text>
              <Text>Slide {index + 1} de {data.slides.length}</Text>
            </View>
          </View>
        </Page>
      ))}
    </Document>
  );
};
